<template>
  <div class="viewer-container">
    <div id="close-viewer">
      <a :href="closeViewerLink">
        <img src="/img/zoom-max.svg" alt="Close">
      </a>
    </div>
    <div id="viewer">
      <img v-if="enableDownload" src="/img/download.svg" alt="Download" id="download" :title="translate('label.modal.download')" @click="showDownloadDialog">
      <img src="/img/zoom-max.svg" alt="+" id="zoom-in">
      <img src="/img/zoom-min.svg" alt="-" id="zoom-out">
      <img src="/img/rotate-left.svg" alt="" id="rotate-left">
      <img src="/img/rotate-right.svg" alt="" id="rotate-right">
    </div>
    <div v-if="!sequenceMode" id="image-navigation">
      <img v-for="(image, index) in images" :key="index" 
        @click="updateTileSource(image)"
        src="/img/dot-item.svg" 
        :class="{'active': image === currentTileSource}"
        :alt="image">
    </div>
    <div v-else id="manuscript-navigation">
      <a id="previous-page" class="">
        <img src="/img/prev.svg" alt="Previous page">
      </a>
      <select 
        name="page_select" 
        id="pageSelect" 
        @change="updateCurrentPageInSequenceMode"
        v-model="selectedPageIndex">
        <option 
          v-for="(image, imagesIndex) in images" 
          :value="imagesIndex" 
          :key="imagesIndex"
          :selected="imagesIndex === selectedPageIndex ? 'selected' : ''">
          {{ imagesIndex + 1 }}
        </option>
      </select>
      <a id="next-page" class="">
        <img src="/img/next.svg" alt="Next page">
      </a>
    </div>
  </div>
</template>

<script>
import OpenSeadragon from 'openseadragon';
export default {
  props: {
    images: { type: Array, required: true },
    enableDownload: { type: Boolean, required: false, default: false },
    sequenceMode: { type: Boolean, required: false, default: false },
    showRotationControls: { type: Boolean, required: false, default: true },
    minZoomLevel: { type: Number, required: false, default: 0.5 },
    defaultZoomLevel: { type: Number, required: false, default: 0.9 }
  },

  data() {
    return {
      currentTileSource: this.images[0],
      selectedPageIndex: 0
    }
  },

  created() {
    EventDispatcher.listen('downloadImage', (imageSize) => {
      let fileNameSuffix = 'original';
      if(imageSize !== 'full') {
        fileNameSuffix = imageSize === 'pct:50' ? 'large' : 'medium';
      }
      const fileName = this.currentTileSource.split('/').pop().replace('.jp2', '_' + fileNameSuffix + '.jpg');
      this.downloadImageWithAxios(this.currentTileSource + '/full/' + imageSize + '/0/default.jpg', fileName);
    });
  },

  mounted() {
     this.viewer = OpenSeadragon({
        id: "viewer",
        zoomInButton: "zoom-in",
        zoomOutButton: "zoom-out",
        rotateLeftButton: "rotate-left",
        rotateRightButton: "rotate-right",
        previousButton: "previous-page",
        nextButton: "next-page",
        showHomeControl: false,
        showFullPageControl: false,
        showRotationControl: this.showRotationControls,
        gestureSettingsTouch: {
          pinchRotate: false
        },
        preserveViewport: true,
        visibilityRatio: 1,
        minZoomLevel: this.minZoomLevel,
        defaultZoomLevel: this.defaultZoomLevel,
        sequenceMode: this.sequenceMode,
        tileSources: this.sequenceMode ? this.images : this.currentTileSource,
    });
    if (this.sequenceMode) {
      let hash = window.location.hash;
      // initialize hash parameter for sequence mode to 1
      if (hash === '') {
        window.location.hash = 1;
      }
      else { // if there is a hash parameter in the URL, validate and use it
        let hashNumber = parseInt(hash.substring(1,));
        // validate hash parameter
        if (hashNumber > 0 && hashNumber <= this.images.length) {
          this.selectedPageIndex = hashNumber - 1;
          this.updateCurrentPageInSequenceMode();
        } else {
          window.location.hash = 1;
        }
      }
    }
    let _this = this;
    this.viewer.addHandler('page', function(event) {
      window.location.hash = (event.page + 1);
      _this.selectedPageIndex = event.page;
      EventDispatcher.fire('updatePreviewImage', _this.images[event.page]);
    });

    window.addEventListener('hashchange', function() {
      _this.selectedPageIndex = parseInt(window.location.hash.substring(1,)) - 1;
      _this.updateCurrentPageInSequenceMode();
    }, false);
  },

  destroyed() {
    window.removeEventListener('hashchange');
  },
  
  computed: {
    closeViewerLink() {
      var bIsReferrerInternal = document.referrer.indexOf("localhost") > -1 
                             || document.referrer.indexOf("e-chartae") > -1;
      if (bIsReferrerInternal) { // go back to previous page if coming from an internal referrer
        return document.referrer;
      } else {                  // go back to homepage if coming from an external page
        return '/'; 
      }
    } 
  },
  methods: {
    updateTileSource(image) {
      this.currentTileSource = image;
      this.viewer.open(image); 
    },

    updateCurrentPageInSequenceMode() {
      this.currentTileSource = this.images[this.selectedPageIndex];
      this.viewer.goToPage(this.selectedPageIndex);
    },

    showDownloadDialog() {
      if(this.enableDownload) {
        EventDispatcher.fire('showDownloadDialog');
      }
    },

    forceFileDownload(response, fileName){
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
    },
      
    downloadImageWithAxios(imageUrl, fileName){
      axios({
        method: 'get',
        url: imageUrl,
        responseType: 'arraybuffer'
      })
      .then(response => {
        this.forceFileDownload(response, fileName)
      })
      .catch(() => console.log('an error occurred when trying to download image'));
    }
  }
   
}
</script>

<style lang="scss" scoped>
  .viewer-container {
    background-color:#000;
    width: 100%;
    height: 100vh;
    overflow: hidden;

    #close-viewer { 
      position:absolute; 
      z-index: 2;
      top: .75rem; 
      right: 1.5rem; 
      z-index: 4000;
      cursor: pointer;
      opacity: 0.7;
      transform: rotate(45deg);
      padding: .25rem;

      &:hover {
        opacity: 1;
      }
    }
    
    #viewer {
      height: 100vh;
      width: 100%;
      margin: 0;
      
      .openseadragon-canvas {
        text-align:center; 
      }

      #zoom-in {
        position: absolute !important;
        bottom: 3.75rem;
        right: 2rem;
        z-index: 2000;
        cursor: pointer;
        opacity: 0.7;

        &:hover {
          opacity: 1;
        }
      }
      #zoom-out {
        position: absolute !important;
        bottom: 1.5rem;
        right: 2rem;
        z-index: 2000;
        cursor: pointer;
        opacity: 0.7;

        &:hover {
          opacity: 1;
        }
      }
      #rotate-left {
        position: absolute !important;
        bottom: 6.5rem; 
        right: 4rem; 
        z-index: 2;
        cursor: pointer;
        opacity: 0.7;
        &:hover {
          opacity: 1;
        }
      }
      #rotate-right {
        position: absolute !important;
        bottom: 6rem; 
        right: 2rem; 
        z-index: 2;
        cursor: pointer;
        opacity:0.7;
        &:hover {
          opacity: 1;
          transform: rotate(360deg);
          transition: all 1.2s ease-in-out 0s;
        }
      }
      #download {
        position: absolute !important;
        bottom: 8.25rem; 
        right: 2rem; 
        z-index: 2;
        cursor: pointer;
        opacity: 0.7;
        &:hover {
          opacity: 1;
        }
      }
    }

    #image-navigation {
      position: relative;
      display: flex;
      justify-content: center;
      
      img {
        position: relative;
        bottom: 2.75rem;
        z-index: 2;
        margin: 0 .75rem;
        cursor: pointer;
        border-radius: 50%;
        opacity:0.5;
        width: 1rem;
        &.active, &:hover {
          opacity:1;
        }
      }
    }

    #manuscript-navigation {
      position: relative;
      display: flex;
      justify-content: center;
      height: 2rem;

      a {
        position: relative;
        bottom: 2.75rem;
        z-index: 2;
        margin: 0 .75rem;
        cursor: pointer;
        border-radius: 50%;
        opacity:0.5;
        width: 1rem;
        &.active, &:hover {
          opacity:1;
        }
      }
      
      #pageSelect {
        position: relative;
        bottom: 2.75rem;
        z-index: 2;
        margin-left: .75rem;
        display: inline-block;
        width: 100px;
        background: rgba(0, 0, 0, .3);
        border: 0;
        border-bottom: 1px solid #fff;
        border-bottom-left-radius: 0 !important;
        border-bottom-right-radius: 0 !important;
        border-top-left-radius: 0 !important;
        border-top-right-radius: 0 !important;
        color: #fff;
      }

    }
  }

  @media (max-width:769px) {
    .viewer-container {
      #close-viewer, #viewer #zoom-in, #viewer #zoom-out,
      #viewer #rotate-left, #viewer #rotate-right, #viewer #download {
        right:.75rem;
      }
    }
  }

  @media (max-width:576px) {
    .viewer-container {
      #image-navigation {
        position: absolute !important;
        left: 0 !important;
        right: 0 !important;
        top: 1.5rem !important;
        text-align: center !important;

        img {
          bottom: 0rem;
        }
      }

      #manuscript-navigation {
        top: 1rem !important;
        select {
          height: 30px;
        }
      }
    } 
  }

  @media print {
    .viewer-container {
      display: none;
    }
  }

</style>
